
import { Component, inject, OnInit, input, output, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule, Location } from '@angular/common';
import { FormBuilder, FormGroup, Validators, ReactiveFormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { TimelineComponent } from 'app/core/components/timeline/timeline.component';
import { VehicleFormComponent } from '../../components/vehicle-form/vehicle-form.component';
import { DocumentGroupComponent } from 'app/feature/management-document/document-group/document-group.component';
import { PartyFormComponent } from 'app/feature/management-parties/components/party-form/party-form.component';
import { PartyOverviewService } from 'app/feature/management-parties/pages/party-overview/party-overview.service';
import { AddPartyService } from 'app/feature/management-parties/pages/add-party/add-party.service';
import Swal from 'sweetalert2';
import { forkJoin, Observable, of, from } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';
import { FormMode } from 'app/core/constants/form-mode';
import { ImageOptimizerService } from 'app/core/service/utils/image-optimizer.service';

@Component({
    selector: 'app-add-vehicle',
    standalone: true,
    imports: [
        CommonModule,
        ReactiveFormsModule,
        TimelineComponent,
        VehicleFormComponent,
        DocumentGroupComponent,
        PartyFormComponent
    ],
    providers: [PartyOverviewService, AddPartyService], // Reutilizamos servicios existentes
    templateUrl: './add-vehicle.component.html',
    changeDetection: ChangeDetectionStrategy.Eager,
    styleUrls: ['./add-vehicle.component.css']
})
export class AddVehicleComponent implements OnInit {
    partyUuidInput = input<string>();
    partyInput = input<any>();
    cancel = output<void>();

    partyUuid!: string;
    currentStep = 2;
    steps = [
        { id: 1, title: 'Datos del Socio' },
        { id: 2, title: 'Datos del Vehículo' },
        { id: 3, title: 'Documentos' }
    ];

    partyForm!: FormGroup;
    vehicleForm: FormGroup;
    documents: any[] = [];
    documentsForm!: FormGroup;
    isSaving = false;
    formMode = FormMode.CREATE; // Usar el modo CREATE
    FormMode = FormMode;

    private fb = inject(FormBuilder);
    private route = inject(ActivatedRoute);
    private router = inject(Router);
    private location = inject(Location);
    private partyService = inject(PartyOverviewService);
    private addPartyService = inject(AddPartyService); // Para uploadDocument
    private imageOptimizer = inject(ImageOptimizerService);

    public Toast = Swal.mixin({
        toast: true, position: 'top-end',
        showConfirmButton: false, timer: 2000, timerProgressBar: true
    });

    constructor() {
        this._initForm();
    }

    private _initForm() {
        this.vehicleForm = this.fb.group({
            partyUuid: [null, [Validators.required]],
            plate: [null, [Validators.required]],
            brandCode: [null, [Validators.required]],
            color: [null, [Validators.required]],
            modelCode: [null, [Validators.required]],
            year: [null, [Validators.required]],
            vin: [null, [Validators.required]],
            km: [null, [Validators.required]],
            vehicleType: [null, [Validators.required]],
            commercialValue: [null, [Validators.required]],
            value: [null, [Validators.required]],
            isTransmissionAutomatic: [1],
            entityCode: [null]
        });
    }

    ngOnInit(): void {
        this._initPartyForm();
        this._resolvePartyUuid();
    }

    private _initPartyForm() {
        this.partyForm = this.fb.group({
            uuid: [null], rut: [null], partyTypeSlug: ['NATURAL'],
            names: [null], birthday: [null], mail: [null],
            mobileNumber: [null], groupSlug: [null], entityCode: [null]
        });
    }

    private _resolvePartyUuid() {
        this.currentStep = 2;
        const party = this.partyInput();
        if (party) {
            this.partyUuid = party.uuid;
            this.partyForm.patchValue(party);
            this.vehicleForm.patchValue({ partyUuid: this.partyUuid });
        } else {
            this._resolvePartyFromRoute();
        }
    }

    private _resolvePartyFromRoute() {
        const inputUuid = this.partyUuidInput();
        const routeUuid = this.route.snapshot.paramMap.get('partyUuid');
        this.partyUuid = inputUuid ?? routeUuid ?? '';
        if (this.partyUuid) {
            this.vehicleForm.patchValue({ partyUuid: this.partyUuid });
        } else {
            this.Toast.fire({ icon: 'error', title: 'No se especificó un socio' });
        }
    }

    onStepChange(stepId: number) {
        // Validar antes de permitir retroceder o avanzar manualmente si es necesario
        // Por ahora solo permitimos avanzar via botón guardar
        // this.currentStep = stepId; 
    }

    submitVehicle() {
        if (this.vehicleForm.valid) {
            this.partyService.createVehicle(this.vehicleForm.value).subscribe({
                next: (res) => this.handleVehicleCreateSuccess(res),
                error: (err) => this.handleVehicleCreateError(err)
            });
        } else {
            const invalidControls = this.getInvalidControls(this.vehicleForm);
            console.warn('Invalid controls:', invalidControls);
            this.Toast.fire({ icon: 'warning', title: 'Formulario inválido: ' + invalidControls.join(', ') });
        }
    }

    private handleVehicleCreateSuccess(res: any) {
        this.Toast.fire({ icon: 'success', title: 'Vehículo creado' });
        this.loadDocuments();
        this.currentStep = 3;
    }

    private handleVehicleCreateError(err: any) {
        const body = err?.error;
        if (err?.status === 409 && body?.code === 'ABANDONED_VEHICLE' && body?.requestUuid) {
            this.promptReactivateAbandonedVehicle(body);
            return;
        }
        console.error(err);
        const msg = body?.error ?? body?.message ?? 'Error al crear vehículo';
        this.Toast.fire({ icon: 'error', title: msg });
    }

    private promptReactivateAbandonedVehicle(body: any) {
        Swal.fire({
            icon: 'question',
            title: 'Solicitud abandonada',
            text: 'Esta patente tiene una solicitud de vehículo nuevo desechada. ¿Desea reactivarla?',
            showCancelButton: true,
            confirmButtonText: 'Sí, reactivar',
            cancelButtonText: 'Cancelar'
        }).then((result) => {
            if (!result.isConfirmed) return;
            Swal.fire({ title: 'Reactivando...', allowOutsideClick: false, didOpen: () => Swal.showLoading() });
            this.addPartyService.reactivateRequest(body.requestUuid).subscribe({
                next: () => {
                    const vehicleUuid = body.vehicleUuid;
                    if (!vehicleUuid) {
                        Swal.close();
                        Swal.fire('Error', 'No se pudo identificar el vehículo reactivado.', 'error');
                        return;
                    }
                    this.addPartyService.updateVehicle(vehicleUuid, {
                        ...this.vehicleForm.value,
                        uuid: vehicleUuid
                    }).subscribe({
                        next: (res) => {
                            Swal.close();
                            this.handleVehicleCreateSuccess(res);
                            this.Toast.fire({ icon: 'success', title: 'Solicitud reactivada' });
                        },
                        error: (err) => {
                            Swal.close();
                            const msg = err?.error?.error ?? err?.error?.message
                                ?? 'Reactivada, pero no se pudo actualizar el vehículo.';
                            Swal.fire('Atención', msg, 'warning');
                        }
                    });
                },
                error: (err) => {
                    Swal.close();
                    const msg = err?.error?.error ?? err?.error?.message ?? 'No se pudo reactivar la solicitud.';
                    Swal.fire('Error', msg, 'error');
                }
            });
        });
    }

    getInvalidControls(form: FormGroup): string[] {
        const invalid = [];
        const controls = form.controls;
        for (const name in controls) {
            if (controls[name].invalid) {
                invalid.push(name);
            }
        }
        return invalid;
    }

    loadDocuments() {
        this.partyService.getDocumentView({ partyUuid: this.partyUuid }).subscribe({
            next: (res: any) => {
                this.documents = res[0] || [];
            },
            error: (err) => console.error(err)
        });
    }

    onDocumentsFormReady(form: FormGroup) {
        this.documentsForm = form;
    }

    submitDocuments() {
        if (!this.documentsForm) {
            console.error('documentsForm no listo');
            return;
        }

        const filesToUpload = this.getFilesToUpload();

        if (filesToUpload.length === 0) {
            this.finish();
            return;
        }

        this.startUploadProcess(filesToUpload);
    }

    private getFilesToUpload() {
        const documentsArray = this.documentsForm.get('documents') as import('@angular/forms').FormArray;
        return documentsArray.controls.filter(control => {
            const fg = control as FormGroup;
            return fg.get('file')?.value && fg.get('uuid')?.value;
        });
    }

    private startUploadProcess(filesToUpload: any[]) {
        this.isSaving = true;
        this.showProcessingModal();
        const uploadObservables = this.createUploadObservables(filesToUpload);

        forkJoin(uploadObservables).subscribe({
            next: (results) => this.handleUploadSuccess(results),
            error: (err) => this.handleUploadError(err)
        });
    }

    private showProcessingModal() {
        Swal.fire({
            title: 'Guardando...',
            text: 'Optimizando imágenes y enviando datos.',
            allowOutsideClick: false,
            didOpen: () => Swal.showLoading()
        });
    }

    private createUploadObservables(files: any[]) {
        return files.map(control => {
            const fg = control as FormGroup;
            const file = fg.get('file')?.value;
            const uuid = fg.get('uuid')?.value;

            return from(this.imageOptimizer.optimize(file)).pipe(
                switchMap(optimizedFile => {
                    const fileToUpload = optimizedFile ?? file;
                    return this.addPartyService.uploadDocument(uuid, fileToUpload);
                }),
                catchError(err => {
                    console.error('Upload Error', err);
                    return of({ error: true });
                })
            );
        });
    }

    private handleUploadSuccess(results: any[]) {
        this.isSaving = false;
        const errors = results.filter((r: any) => r && r.error).length;
        if (errors === 0) {
            this.showSuccessModal();
        } else {
            Swal.fire('Atención', `Finalizado con ${errors} errores`, 'warning').then(() => this.finish());
        }
    }

    private showSuccessModal() {
        Swal.fire({
            title: '¡Listo!',
            text: 'Documentos guardados',
            icon: 'success',
            timer: 2000,
            timerProgressBar: true,
            allowOutsideClick: false,
            showConfirmButton: false
        }).then(() => this.finish());
    }

    private handleUploadError(err: any) {
        this.isSaving = false;
        Swal.fire('Error', 'Error inesperado', 'error');
    }

    finish() {
        if (this.partyUuidInput()) {
            this.cancel.emit();
        } else {
            this.router.navigate(['/parties'], { queryParams: { partyUuid: this.partyUuid } });
        }
    }

    goBack() {
        if (this.partyUuidInput()) {
            this.cancel.emit();
        } else {
            this.router.navigate(['/parties'], { queryParams: { partyUuid: this.partyUuid } });
        }
    }
}
